import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Sparkles, MessageCircle, Lightbulb, ArrowRight, Wand2, BadgeCheck, Users, ChevronRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import CtaButton from "@/components/CtaButton";

const TINTS = [
  { from: "from-sky", to: "to-skylight", accent: "bg-sky", chip: "bg-sky/15 text-sky border-sky/30" },
  { from: "from-rose", to: "to-roselight", accent: "bg-rose", chip: "bg-rose/15 text-rose border-rose/30" },
  { from: "from-lemon", to: "to-lemonlight", accent: "bg-amber-500", chip: "bg-amber-500/15 text-amber-700 border-amber-400/40" },
];

const OPTION_LETTERS = ["A", "B", "C"];

export default function ProjectsShowcase({ projects, testId = "projects-showcase", onEnquire }) {
  const [selected, setSelected] = useState(null);
  if (!projects || projects.length === 0) return null;

  // `interest` (a project title, or undefined) is threaded through to the
  // enquiry dialog so it opens with the right idea already picked.
  const onTalk = (interest) => onEnquire && onEnquire(interest);

  return (
    <div data-testid={testId} className="relative">
      <div className="absolute -top-16 right-0 w-80 h-80 bg-violet/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="space-y-10 md:space-y-12 relative">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6 auto-rows-fr items-stretch">
          {projects.map((p, i) => {
            const tint = TINTS[i % TINTS.length];
            const letter = OPTION_LETTERS[i % OPTION_LETTERS.length];
            const isSelected = selected === i;
            return (
              <Reveal key={p.title} delay={i * 0.07}>
                <motion.button
                  type="button"
                  onClick={() => setSelected(isSelected ? null : i)}
                  whileHover={{ y: -5 }}
                  whileTap={{ scale: 0.992 }}
                  className={`group relative text-left w-full h-full rounded-[2rem] overflow-hidden border-2 transition-all duration-400 flex flex-col ${
                    isSelected ? "border-terracotta shadow-2xl shadow-terracotta/20 -translate-y-1" : "border-transparent hover:border-line shadow-lg hover:shadow-xl"
                  }`}
                >
                  <div className={`relative shrink-0 aspect-[5/4] p-2.5 bg-gradient-to-br ${tint.from} ${tint.to}`}>
                    <div className="relative h-full w-full rounded-[1.5rem] overflow-hidden">
                      <img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute top-3.5 left-3.5 flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 rounded-full bg-surface/95 backdrop-blur px-3.5 py-1.5 border border-line shadow-sm`}>
                          <span className={`w-6 h-6 rounded-full ${tint.accent} text-white flex items-center justify-center font-black text-[11px]`}>{letter}</span>
                          <span className="font-sans text-[11px] font-bold uppercase tracking-wider text-ink/80">{p.tag.split("·")?.[1]?.trim() || p.tag}</span>
                        </span>
                      </div>
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="absolute top-3.5 right-3.5 w-9 h-9 rounded-full bg-terracotta text-white flex items-center justify-center shadow-lg"
                        >
                          <Check size={16} />
                        </motion.div>
                      )}
                    </div>
                  </div>

                  <div className="bg-surface p-5 md:p-6 relative flex-1 flex flex-col min-h-0">
                    <div className={`absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r ${tint.from} ${tint.to} opacity-80`} aria-hidden="true" />
                    <h4 className="font-display font-bold text-xl md:text-[22px] text-ink leading-tight min-h-[3.5rem]">{p.title}</h4>
                    <p className="text-graphite mt-3 leading-relaxed text-[14.5px]">{p.summary}</p>
                    {p.skills && p.skills.length > 0 && (
                      <ul className="mt-4 space-y-2 flex-1 min-h-[5.25rem]">
                        {p.skills.map((s, j) => (
                          <li key={j} className="flex items-start gap-2.5 text-[13.5px] text-ink/85">
                            <Check size={14} className="mt-0.5 shrink-0 text-emerald" />
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className={`mt-5 inline-flex self-start items-center gap-1.5 rounded-full px-3 py-1.5 border text-[11px] font-bold uppercase tracking-wider ${tint.chip}`}>
                      <BadgeCheck size={12} /> <span>Pick option {letter}</span>
                    </div>
                  </div>
                </motion.button>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.05}>
          <div className="relative rounded-[2rem] border border-dashed border-terracotta/40 bg-gradient-to-br from-terracotta/[0.04] via-amber-500/[0.04] to-emerald/[0.04] p-6 md:p-8 overflow-hidden">
            <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full bg-terracotta/10 blur-3xl pointer-events-none" aria-hidden="true" />
            <div className="absolute -bottom-10 -left-10 w-52 h-52 rounded-full bg-emerald/10 blur-3xl pointer-events-none" aria-hidden="true" />

            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
              <div className="lg:col-span-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="inline-flex w-11 h-11 rounded-2xl bg-gradient-to-br from-terracotta to-amber-500 text-white items-center justify-center shadow-md">
                    <Wand2 size={20} />
                  </span>
                  <span className="font-sans text-xs font-bold text-terracotta uppercase tracking-wider">Don't see exactly what you want?</span>
                </div>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-ink leading-tight">
                  Bring your own idea — build whatever matters to <span className="text-terracotta">you</span>.
                </h3>
                <p className="text-graphite mt-4 leading-relaxed text-[15px] max-w-xl">
                  The options above are real projects we've built with students before — pick one, mix them, or come with a completely different idea.
                  A side project for your business, a portfolio piece, a tool for your team, or an app you've always wanted — the course adapts to it.
                </p>

                <div className="mt-5 flex flex-wrap gap-2.5">
                  {["SaaS dashboard", "AI chatbot", "Personal portfolio", "Booking site", "Internal tool", "Mobile-style app"].map((t) => (
                    <span key={t} className="inline-flex items-center gap-1.5 rounded-full bg-surface px-3 py-1.5 text-[12px] font-semibold text-ink/80 border border-line/80 shadow-sm">
                      <Lightbulb size={12} className="text-amber-500" /> {t}
                    </span>
                  ))}
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-surface px-3 py-1.5 text-[12px] font-semibold text-terracotta border border-terracotta/30 shadow-sm">
                    + Your idea
                  </span>
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="rounded-3xl bg-surface border border-line/80 shadow-sm p-6 md:p-7">
                  <div className="flex items-start gap-4">
                    <span className="shrink-0 w-12 h-12 rounded-2xl bg-emeraldlight text-emerald flex items-center justify-center shadow-inner">
                      <Users size={22} />
                    </span>
                    <div>
                      <p className="font-sans text-xs font-bold text-terracotta uppercase tracking-wider">Talk to us</p>
                      <h4 className="font-display font-bold text-xl text-ink leading-tight mt-1">Not sure what to build? We'll help you pick.</h4>
                      <p className="text-graphite mt-2 leading-relaxed text-[14.5px]">
                        Tell us a little about your goals when you enquire — career, business, curiosity, or something else — and we'll recommend the right project shape before the batch even starts.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="rounded-2xl bg-stone p-4 border border-line/60">
                      <div className="flex items-center gap-2 text-emerald mb-1.5">
                        <Check size={14} /> <span className="font-bold text-xs uppercase tracking-wider">One project focus</span>
                      </div>
                      <p className="text-[13px] text-ink/80 leading-snug">Go deep on a single polished build you can show people.</p>
                    </div>
                    <div className="rounded-2xl bg-stone p-4 border border-line/60">
                      <div className="flex items-center gap-2 text-sky mb-1.5">
                        <Check size={14} /> <span className="font-bold text-xs uppercase tracking-wider">Taste of everything</span>
                      </div>
                      <p className="text-[13px] text-ink/80 leading-snug">Ship multiple smaller builds across different categories.</p>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    {onEnquire ? (
                      <CtaButton onEnquire={() => onTalk("My own idea")} full label="Build your own — talk to us" size="md" />
                    ) : (
                      <span className="font-sans font-bold text-terracotta text-sm inline-flex items-center gap-1.5">
                        Build your own — talk to us <ChevronRight size={16} />
                      </span>
                    )}
                    <div className="flex items-center gap-2 text-xs text-graphite font-semibold px-2">
                      <MessageCircle size={14} className="text-terracotta shrink-0" />
                      <span>No payment now · we confirm the plan with you directly.</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <AnimatePresence>
          {selected !== null && (
            <Reveal>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 12 }}
                transition={{ duration: 0.4 }}
                className="rounded-3xl border border-emerald/30 bg-emerald/[0.05] p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
              >
                <div className="shrink-0 w-12 h-12 rounded-2xl bg-emerald text-white flex items-center justify-center shadow-md">
                  <Sparkles size={20} />
                </div>
                <div className="flex-1">
                  <p className="font-sans text-xs font-bold text-emerald uppercase tracking-wider">Option {OPTION_LETTERS[selected % OPTION_LETTERS.length]} picked</p>
                  <h4 className="font-display font-bold text-xl text-ink mt-1">
                    You picked <span className="text-terracotta">{projects[selected]?.title}</span> — great choice.
                  </h4>
                  <p className="text-graphite text-[14.5px] mt-1 leading-relaxed">Enquire below and we'll tailor the program around this project (or swap it any time before the batch starts).</p>
                </div>
                {onEnquire && (
                  <CtaButton
                    onEnquire={() => onTalk(projects[selected]?.title)}
                    label={`Enquire — Option ${OPTION_LETTERS[selected % OPTION_LETTERS.length]}`}
                  />
                )}
              </motion.div>
            </Reveal>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
